<template>
    <header class="header">
        <div class="navbar">
            <div class="flex-1 flex">
                <div class="navbar-item">
                    <fold />
                </div>
                <div class="navbar-item">
                    <refresh />
                </div>
                <div
                    class="flex items-center px-2"
                    v-if="!isMobile && settingStore.showCrumb"
                >
                    <breadcrumb />
                </div>
            </div>
            <div class="flex">
                <div class="navbar-item" v-if="!isMobile">
                    <full-screen />
                </div>
                <!-- 软件更新提示 -->
                <div
                    class="navbar-item cursor-pointer mr-6 ml-1"
                    v-if="isRootAdmin"
                    @click="openUpdateDialog"
                >
                    <el-tooltip :content="updateTooltip" placement="bottom">
                        <div class="relative flex items-center px-2">
                            <el-badge
                                :value="
                                    updateState.has_update && updateState.ok
                                        ? 'new'
                                        : ''
                                "
                            >
                                <icon name="el-icon-Upload" :size="18" />
                            </el-badge>
                        </div>
                    </el-tooltip>
                </div>
                <div class="navbar-item">
                    <user-drop-down />
                </div>
                <div class="navbar-item">
                    <setting />
                </div>
            </div>
        </div>
        <multiple-tabs v-if="settingStore.openMultipleTabs" />

        <el-dialog
            v-model="dialogVisible"
            title="软件更新"
            width="520px"
            destroy-on-close
            append-to-body
        >
            <div class="software-update-dialog">
                <p class="mb-2">
                    <span class="text-gray-500">当前版本：</span>
                    {{ updateState.current_version || '—' }}
                </p>
                <el-alert
                    v-if="!updateState.ok && updateState.message"
                    type="warning"
                    :closable="false"
                    class="mb-3"
                    :title="updateState.message"
                />
                <template
                    v-else-if="updateState.has_update && updateState.release"
                >
                    <el-alert
                        type="success"
                        :closable="false"
                        class="mb-3"
                        title="检测到可更新的新版本"
                    />
                    <p class="mb-1">
                        <span class="text-gray-500">新版本：</span>
                        <el-tag type="success">{{
                            updateState.release.version
                        }}</el-tag>
                    </p>
                    <p
                        class="mb-2 text-sm text-gray-500"
                        v-if="
                            updateState.release.require_from_version !==
                            undefined
                        "
                    >
                        需从版本「{{
                            updateState.release.require_from_version ||
                            '(初始)'
                        }}」升级
                    </p>
                    <div
                        class="changelog-box"
                        v-if="updateState.release.changelog"
                    >
                        <div class="text-gray-500 text-sm mb-1">更新说明</div>
                        <pre class="changelog-pre">{{
                            updateState.release.changelog
                        }}</pre>
                    </div>
                    <p v-else class="text-gray-400 text-sm mb-2">
                        更新说明：暂无
                    </p>
                    <p class="text-sm text-gray-500 mb-2">
                        更新方式：
                        <el-tooltip
                            content="系统将自动更新，也可点击「立即更新」提前安装"
                            v-if="updateState.release.update_type === 2"
                        >
                            <el-tag type="warning"> 系统自动更新 </el-tag>
                        </el-tooltip>

                        <el-tag type="success" v-else> 用户手动更新 </el-tag>
                    </p>
                </template>
                <el-empty
                    v-else-if="updateState.ok"
                    description="当前已是最新版本"
                    :image-size="80"
                />
            </div>
            <template #footer>
                <el-button @click="dialogVisible = false">关闭</el-button>
                <el-button
                    type="primary"
                    :loading="applyLoading"
                    :disabled="!updateState.has_update || !updateState.ok"
                    @click="onApplyUpdate"
                >
                    立即更新
                </el-button>
            </template>
        </el-dialog>
    </header>
</template>

<script setup lang="ts">
import {
    softwareUpdateCheck,
    softwareUpdateApply,
    type SoftwareUpdateCheckData
} from '@/api/software_update'
import useUserStore from '@/stores/modules/user'
import useAppStore from '@/stores/modules/app'
import Fold from './fold.vue'
import Refresh from './refresh.vue'
import Breadcrumb from './breadcrumb.vue'
import FullScreen from './full-screen.vue'
import UserDropDown from './user-drop-down.vue'
import Setting from '../setting/index.vue'
import MultipleTabs from './multiple-tabs.vue'

import useSettingStore from '@/stores/modules/setting'
import { ThemeEnum } from '@/enums/appEnums'

import defaultSetting from '@/config/setting'
import feedback from '@/utils/feedback'
import { ElMessageBox } from 'element-plus'

const appStore = useAppStore()
const userStore = useUserStore()
const isMobile = computed(() => appStore.isMobile)
const settingStore = useSettingStore()
const sideTheme = computed(() => settingStore.sideTheme)

const isRootAdmin = computed(() => Number(userStore.userInfo?.root || 0) === 1)

const isAark = computed(() => {
    return sideTheme.value == ThemeEnum.DARK
})

const navbarBgc = computed(() => {
    return isAark.value ? defaultSetting.sideDarkColor : 'var(--el-bg-color)'
})

const navbarHoverBgc = computed(() => {
    return isAark.value ? '#214885' : 'var(--el-color-primary-light-9)'
})

const iconColor = computed(() => {
    return isAark.value ? '#fff' : 'inherit'
})

const textColor = computed(() => {
    return isAark.value ? '#c4d1e6' : 'var(--el-text-color-regular)'
})

const defaultCheckState = (): SoftwareUpdateCheckData => ({
    ok: false,
    current_version: '',
    has_update: false,
    release: null
})

const updateState = reactive<SoftwareUpdateCheckData>(defaultCheckState())
const dialogVisible = ref(false)
const applyLoading = ref(false)
const isAutoRelease = computed(
    () =>
        !!(
            updateState.has_update &&
            updateState.release &&
            updateState.release.update_type === 2
        )
)

const updateTooltip = computed(() => {
    if (!updateState.ok && updateState.message) {
        return updateState.message
    }
    if (updateState.has_update) {
        if (isAutoRelease.value) {
            return `新版本 ${
                updateState.release?.version || ''
            } 可自动更新，也可手动立即更新`
        }
        return `有新版本 ${updateState.release?.version || ''}，点击查看`
    }
    return '软件更新'
})

async function fetchUpdateCheck() {
    if (!isRootAdmin.value) {
        Object.assign(updateState, defaultCheckState())
        return
    }
    try {
        const data = await softwareUpdateCheck()
        Object.assign(updateState, data)
    } catch {
        // 请求失败时保留上次状态或静默
    }
}

function openUpdateDialog() {
    if (!isRootAdmin.value) {
        return
    }
    dialogVisible.value = true
    fetchUpdateCheck()
}

async function onApplyUpdate() {
    try {
        await ElMessageBox.confirm(
            '将执行服务器端更新（下载包、执行 SQL、覆盖目录），耗时可能较长，请勿关闭页面。是否继续？',
            '确认更新',
            { type: 'warning' }
        )
    } catch {
        return
    }
    applyLoading.value = true
    try {
        await softwareUpdateApply()
        // feedback.msgSuccess('更新已完成')
        dialogVisible.value = false
        await fetchUpdateCheck()
    } catch (e: any) {
        const msg =
            e?.msg ||
            e?.message ||
            '更新失败，请查看日志或手动执行 php think version_updating'
        feedback.msgError(msg)
    } finally {
        applyLoading.value = false
    }
}

let pollTimer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
    if (!isRootAdmin.value) {
        return
    }
    fetchUpdateCheck()
    pollTimer = setInterval(fetchUpdateCheck, 10 * 60 * 1000)
})

onBeforeUnmount(() => {
    if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
    }
})
</script>

<style lang="scss">
.navbar {
    height: var(--navbar-height);
    @apply flex px-2 bg-body;
    background-color: v-bind('navbarBgc');
    .navbar-item {
        @apply h-full  flex justify-center items-center;
        color: v-bind('iconColor');
        &:hover {
            background-color: v-bind('navbarHoverBgc');
        }
    }
    .el-breadcrumb__inner {
        color: v-bind('textColor') !important;
    }
    .user-name,
    .more {
        color: v-bind('textColor') !important;
    }
    .text-danger-imp {
        color: #f56c6c;
        font-weight: 700;
    }
    .text-warning-imp {
        color: #eebe77;
        font-weight: 700;
    }
    .text-success-imp {
        // color: #67c23a;
    }
}

.software-update-dialog {
    .changelog-box {
        max-height: 220px;
        overflow: hidden;
    }
    .changelog-pre {
        margin: 0;
        padding: 8px 10px;
        max-height: 200px;
        overflow: auto;
        font-size: 13px;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
        background: var(--el-fill-color-light);
        border-radius: 4px;
    }
}
</style>
